<script setup lang="ts">
  import type { Ref } from 'vue'
  import { computed, ref, watch } from 'vue'
  import { GITypeDisplay, type GIPointCollection } from '@/stores/type/giPoint.type'
  import GIPoint from '@/components/projectDetail/GIDrawer/GIPoint.vue'
  import GICollection from '@/components/projectDetail/GIDrawer/GICollection.vue'
  import { debounce } from 'lodash'
  import { useAuthorizationStore } from '@/stores/authorization'
  import NoDataAvailableText from '@/components/projectDetail/components/NoDataAvailableText.vue'
  import SecondaryButton from '@/components/shared/SecondaryButton.vue'
  import { getGIDipslayName } from '@/utils/giPoint'

  const props = defineProps({
    isSelecting: { type: Boolean, required: false, default: false }
  })
  const giPointsStore = useGIPointsStore()
  const giDataUploadCollectionsStore = useGIDataUploadCollectionStore()
  const sectionLinesStore = useSectionLinesStore()
  const authorizationStore = useAuthorizationStore()

  const { deleteDialog, selectedItems: selectedGIPoints } = storeToRefs(giPointsStore)
  const { items: giDataCollections, selectedItemIDs: selectedGIDataCollections } = storeToRefs(
    giDataUploadCollectionsStore
  )
  const { selectedItems: selectedSectionLines, showSelectMap: showSelectSectionLineMap } =
    storeToRefs(sectionLinesStore)

  const headers = [
    { title: 'Name', key: 'properties.location_id' },
    { title: 'Survey Company', key: 'properties.survey_company' },
    { title: 'Survey Date', key: 'properties.survey_datestring' },
    { title: 'GI Type', key: 'properties.generic_gi_type' },
    { title: 'End Depth', key: 'properties.end_depth' },
    { title: 'Z Coordinates', key: 'geometry.coordinates' }
  ]

  const openedGroups: Ref<number[]> = ref([])
  const searchQuery = ref('')
  const selectedCollections = ref(new Set())
  const handleSelected = (id: number, isSelected: boolean) => {
    if (isSelected) {
      selectedCollections.value.add(id)
    } else {
      selectedCollections.value.delete(id)
    }
  }
  const debouncedSearchQuery = computed({
    get() {
      return searchQuery.value
    },
    set: debounce((query) => {
      searchQuery.value = query
    }, 300)
  })

  const selectedFilter = ref(null)
  const filterItems = [
    { title: 'Borehole', value: GITypeDisplay.BOREHOLE },
    { title: 'CPT', value: GITypeDisplay.CPT }
  ]
  const selectAll = ref(false)
  const toggleSelectAll = () => {
    if (selectAll.value) {
      giDataUploadCollectionsStore.selectAllGIPointCollections()
    } else {
      giDataUploadCollectionsStore.clearSelectedGIPointCollections()
    }
  }

  const filteredGIPointCollections = computed(() => {
    if ((!searchQuery.value || searchQuery.value === '') && !selectedFilter.value) {
      return giDataCollections.value
    }

    return giDataCollections.value.filter((collection: GIPointCollection) => {
      return collection.gipoint_set.features.some((giPoint) => {
        const locationId = giPoint.properties?.location_id?.toLowerCase()
        // const giType = giPoint.properties?.generic_gi_type
        const giType = getGIDipslayName(giPoint)
        const searchMatch =
          searchQuery.value && searchQuery.value !== ''
            ? locationId.includes(searchQuery.value.toLowerCase())
            : true
        const filterMatch = selectedFilter.value ? giType === selectedFilter.value : true
        return searchMatch && filterMatch
      })
    })
  })

  const filteredGIPointsByType = (giPointCollection: GIPointCollection) => {
    const selectedType = selectedFilter.value
    const searchTerm = searchQuery.value?.toLowerCase()

    return giPointCollection.gipoint_set.features.filter((feature) => {
      const locationId = feature.properties?.location_id?.toLowerCase()
      const giType = getGIDipslayName(feature)
      const searchMatch = searchTerm && searchTerm !== '' ? locationId.includes(searchTerm) : true
      const filterMatch = selectedType ? giType === selectedType : true
      return searchMatch && filterMatch
    })
  }

  const canDelete = computed(() =>
    authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_gidatauploadcollection']
    })
  )

  const clearFilters = () => {
    searchQuery.value = ''
    selectedFilter.value = null
  }

  watch(deleteDialog, () => {
    if (!deleteDialog.value) {
      giPointsStore.setDeleteItem({})
    }
  })

  watch(giDataCollections, (newCollections: GIPointCollection[]) => {
    newCollections.forEach((giPointCollection: GIPointCollection, index) => {
      // open the first v-list-group by default
      if (index === 0) openedGroups.value.push(giPointCollection.id)
    })
  })

  watch(
    filteredGIPointCollections,
    (newFilteredCollections: GIPointCollection[]) => {
      if (searchQuery.value || selectedFilter.value) {
        openedGroups.value = newFilteredCollections.map((collection) => collection.id)
      } else {
        openedGroups.value = newFilteredCollections.length > 0 ? [newFilteredCollections[0].id] : []
      }
    },
    { immediate: true }
  )

  watch(
    () => selectedGIDataCollections.value,
    (newSelectedItems) => {
      if (giDataUploadCollectionsStore.items.length === 0) {
        selectAll.value = false
      } else {
        selectAll.value = newSelectedItems.length === giDataUploadCollectionsStore.items.length
      }
    },
    { deep: true }
  )
</script>

<template>
  <v-lazy height="100%">
    <v-list
      density="compact"
      class="overflow-y-auto pt-0 pb-10"
      v-model:opened="openedGroups"
      style="max-height: 91%"
    >
      <div
        class="d-flex justify-center align-center ga-2 pt-2 pb-2 px-2 bg-white"
        style="top: 0; position: sticky; z-index: 1000"
      >
        <v-text-field
          v-model="debouncedSearchQuery"
          color="primary"
          append-inner-icon="mdi-magnify"
          density="comfortable"
          label="Search..."
          hide-details
          single-line
          clearable
        ></v-text-field>
        <v-select
          :items="filterItems"
          v-model="selectedFilter"
          color="primary"
          density="comfortable"
          hide-details
          clearable
          style="max-width: 150px"
          label="GI Type"
        ></v-select>
        <SecondaryButton prepend-icon="mdi-close" @click.stop="clearFilters">
          Clear all filters
        </SecondaryButton>
        <template v-if="canDelete">
          <v-divider vertical thickness="2" />
          <SecondaryButton
            prepend-icon="mdi-delete"
            @click="giDataUploadCollectionsStore.toggleDeleteDialog()"
            :disabled="!selectedGIDataCollections.length"
          >
            Delete
          </SecondaryButton>
        </template>
      </div>
      <div class="d-flex align-center" v-if="!searchQuery && !props.isSelecting">
        <v-checkbox
          v-model="selectAll"
          @change="toggleSelectAll"
          :disabled="!giDataCollections.length"
          color="primary"
          class="px-1"
          hide-details
        ></v-checkbox>
        <span>Select all</span>
      </div>
      <v-list-group
        v-for="giPointCollection in filteredGIPointCollections"
        :key="giPointCollection.id"
        :value="giPointCollection.id"
        subgroup
      >
        <template v-slot:activator="{ props: menuProps }">
          <v-list-item
            :nav="true"
            :v-ripple="true"
            class="w-100 py-0 my-0 list-item rounded-0"
            :class="{ 'selected-item': selectedCollections.has(giPointCollection.id) }"
            v-bind="menuProps"
          >
            <GICollection
              :is-selecting="props.isSelecting"
              :store="giDataUploadCollectionsStore"
              :item="giPointCollection"
              @edit="giDataUploadCollectionsStore.toggleUploadDialog(giPointCollection)"
              @delete="giDataUploadCollectionsStore.toggleDeleteDialog(giPointCollection, true)"
              @selected="(isSelected) => handleSelected(giPointCollection.id, isSelected)"
            />
          </v-list-item>
        </template>
        <v-data-table
          :items="filteredGIPointsByType(giPointCollection)"
          item-value="id"
          :show-select="
            selectedSectionLines[0] &&
            showSelectSectionLineMap[selectedSectionLines[0].id] === 'create'
          "
          :class="{ 'selected-item': selectedCollections.has(giPointCollection.id) }"
          :headers="headers"
          :hover="true"
          v-model="selectedGIPoints"
          items-per-page="-1"
          no-data-text="No GI Points available."
          class="row-pointer"
          density="compact"
        >
          <template v-slot:item="{ item }">
            <GIPoint
              :item="item"
              :selectedItems="selectedGIPoints"
              :show-select="
                selectedSectionLines[0] &&
                showSelectSectionLineMap[selectedSectionLines[0].id] === 'create'
              "
              @update:selectedItems="(val) => giPointsStore.addSelectedGIPoint(val)"
              @click="giPointsStore.setCurrentItem(item)"
            />
          </template>
        </v-data-table>
      </v-list-group>
      <NoDataAvailableText v-if="!giDataCollections.length || !filteredGIPointCollections.length">
        There are no ground investigation data available.
      </NoDataAvailableText>
    </v-list>
  </v-lazy>
</template>

<style scoped>
  :deep(.v-data-table-footer) {
    display: none;
  }

  .row-pointer >>> tbody tr :hover {
    cursor: pointer;
  }

  :deep(.v-list-item__prepend) {
    width: 20px;
    margin-left: 4px;
    margin-right: 4px;
  }

  .selected-item {
    background-color: #f4fcff;
  }

  .list-item:hover {
    background-color: #f4fcff;
  }

  :deep(.v-table__wrapper) table tbody tr:hover {
    background-color: #f4fcff;
  }
</style>
